import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  MenuItem,
  Select,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab,
  Checkbox,
  Toolbar,
  Autocomplete,
  TextField,
} from "@mui/material";
import {
  Sync as SyncIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  GroupAdd as GroupAddIcon,
  CompareArrows as CompareArrowsIcon,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material";
import { Layout } from "../Layout";
import { Client } from "../../services/client.service";
import api from "../../services/api";

interface CCMCareProvider {
  _id: string;
  fname: string;
  lname: string;
  email?: string;
  username?: string;
}

interface ReconciliationMatch {
  patientID: number;
  firstname: string;
  lastname: string;
  dob: string;
  externalLookup: string;
  tnsHasExternalSystemId: boolean;
  ccmPatientId?: string;
  ccmEmail?: string;
  matchStatus: "matched" | "tns-only" | "ccm-only";
  diabetesProgram?: boolean;
  weightProgram?: boolean;
  hypertensionProgram?: boolean;
}

interface CCMOnlyPatient {
  ccmPatientId: string;
  firstname: string;
  lastname: string;
  dob: string;
  email?: string;
}

interface ReconciliationResult {
  entityId: number;
  entityName: string;
  matched: ReconciliationMatch[];
  tnsOnly: ReconciliationMatch[];
  ccmOnly: CCMOnlyPatient[];
  storedCount: number;
  errors: string[];
}

interface CompareRow {
  memberId: string;
  lastname: string;
  firstname: string;
}

interface CompareResult {
  aOnly: CompareRow[];
  bOnly: CompareRow[];
  inBoth: CompareRow[];
  fileAName: string;
  fileBName: string;
}

const PatientReconciliation: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ReconciliationResult | null>(null);
  const [error, setError] = useState("");
  const [tabValue, setTabValue] = useState(0);

  // Bulk operations state
  const [selectedPatients, setSelectedPatients] = useState<number[]>([]);
  const [careProviders, setCareProviders] = useState<CCMCareProvider[]>([]);
  const [selectedCareProvider, setSelectedCareProvider] =
    useState<CCMCareProvider | null>(null);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [bulkSuccess, setBulkSuccess] = useState("");

  // Program filter state
  const [programFilters, setProgramFilters] = useState({
    diabetes: true,
    weight: true,
    hypertension: true,
  });

  // Step 2 mode selection
  const [step2Mode, setStep2Mode] = useState<"reconcile" | "compare" | null>(
    null,
  );

  // Compare Enrollment Files state
  const [fileA, setFileA] = useState<File | null>(null);
  const [fileB, setFileB] = useState<File | null>(null);
  const [compareResult, setCompareResult] = useState<CompareResult | null>(
    null,
  );
  const [compareError, setCompareError] = useState("");
  const [compareTabValue, setCompareTabValue] = useState(0);

  // Load clients on mount
  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const response = await api.get("/import/clients");
      if (response.data.success) {
        // Only show PHM (1008) and WPH (983) for now
        const ccmClients = response.data.clients.filter((client: Client) =>
          [983, 1008].includes(client.id),
        );
        setClients(ccmClients);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load clients");
    }
  };

  const parseCsvFile = (file: File): Promise<CompareRow[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text
            .split(/\r?\n/)
            .map((l) => l.trim())
            .filter((l) => l.length > 0);
          if (lines.length < 2) {
            resolve([]);
            return;
          }
          // Skip header row, parse first 3 columns as memberId, lastname, firstname
          const rows: CompareRow[] = lines
            .slice(1)
            .map((line) => {
              // Handle quoted fields simply by splitting on comma outside quotes
              const cols = line
                .split(",")
                .map((c) => c.trim().replace(/^"|"$/g, ""));
              return {
                memberId: (cols[0] || "").trim().toLowerCase(),
                lastname: (cols[1] || "").trim().toLowerCase(),
                firstname: (cols[2] || "").trim().toLowerCase(),
              };
            })
            .filter((r) => r.memberId.length > 0);
          resolve(rows);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsText(file);
    });
  };

  const handleCompare = async () => {
    if (!fileA || !fileB) {
      setCompareError("Please select both CSV files");
      return;
    }
    try {
      setCompareError("");
      setCompareResult(null);
      const [rowsA, rowsB] = await Promise.all([
        parseCsvFile(fileA),
        parseCsvFile(fileB),
      ]);

      // Build a key from memberId only (columns 1) for fast lookup
      const keyA = new Map<string, CompareRow>();
      rowsA.forEach((r) => keyA.set(r.memberId, r));
      const keyB = new Map<string, CompareRow>();
      rowsB.forEach((r) => keyB.set(r.memberId, r));

      const inBoth: CompareRow[] = [];
      const aOnly: CompareRow[] = [];
      const bOnly: CompareRow[] = [];

      rowsA.forEach((r) => {
        if (keyB.has(r.memberId)) {
          inBoth.push(r);
        } else {
          aOnly.push(r);
        }
      });

      rowsB.forEach((r) => {
        if (!keyA.has(r.memberId)) {
          bOnly.push(r);
        }
      });

      // Sort all groups by lastname then firstname
      const sortRows = (arr: CompareRow[]) =>
        arr.sort(
          (a, b) =>
            a.lastname.localeCompare(b.lastname) ||
            a.firstname.localeCompare(b.firstname),
        );

      setCompareResult({
        aOnly: sortRows(aOnly),
        bOnly: sortRows(bOnly),
        inBoth: sortRows(inBoth),
        fileAName: fileA.name,
        fileBName: fileB.name,
      });
      setCompareTabValue(0);
    } catch (err: any) {
      setCompareError(err.message || "Failed to compare files");
    }
  };

  const handleReconcile = async () => {
    if (!selectedEntity) {
      setError("Please select an entity");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setResult(null);

      console.log(
        `[Frontend] Calling API: /utilities/reconcile-patients/${selectedEntity}`,
      );
      const response = await api.get(
        `/utilities/reconcile-patients/${selectedEntity}`,
      );

      console.log(`[Frontend] API Response:`, response.data);
      setResult(response.data);
      setSelectedPatients([]); // Reset selection when new results load

      // Load care providers for bulk operations
      if (response.data.matched.length > 0) {
        loadCareProviders(selectedEntity);
      }
    } catch (err: any) {
      console.error(`[Frontend] API Error:`, err);
      console.error(`[Frontend] Error response:`, err.response);
      setError(
        err.response?.data?.error ||
          err.response?.data?.details ||
          "Failed to reconcile patients",
      );
    } finally {
      setLoading(false);
    }
  };

  const loadCareProviders = async (entityId: number) => {
    try {
      const response = await api.get(`/patients/ccm/careproviders/${entityId}`);
      setCareProviders(response.data.careProviders || []);
    } catch (err: any) {
      console.error("Failed to load care providers:", err);
    }
  };

  const handleSelectAll = () => {
    if (!result) return;
    const filteredPatients = filterPatientsByPrograms(result.matched);
    if (selectedPatients.length === filteredPatients.length) {
      setSelectedPatients([]);
    } else {
      setSelectedPatients(filteredPatients.map((p) => p.patientID));
    }
  };

  const handleTogglePatient = (patientId: number) => {
    setSelectedPatients((prev) =>
      prev.includes(patientId)
        ? prev.filter((id) => id !== patientId)
        : [...prev, patientId],
    );
  };

  const handleBulkAssign = async () => {
    if (
      !selectedCareProvider ||
      selectedPatients.length === 0 ||
      !selectedEntity
    ) {
      return;
    }

    try {
      setBulkAssigning(true);
      setError("");
      setBulkSuccess("");

      const promises = selectedPatients.map(async (patientId) => {
        try {
          await api.post(`/patients/${patientId}/ccm/careproviders`, {
            entityId: selectedEntity,
            careProviderIds: [selectedCareProvider._id],
          });
          return { patientId, success: true };
        } catch (err: any) {
          return {
            patientId,
            success: false,
            error: err.response?.data?.error || "Failed",
          };
        }
      });

      const results = await Promise.all(promises);
      const successCount = results.filter((r) => r.success).length;
      const failCount = results.filter((r) => !r.success).length;

      if (failCount === 0) {
        setBulkSuccess(
          `Successfully assigned ${successCount} patient(s) to ${selectedCareProvider.fname} ${selectedCareProvider.lname}`,
        );
        setSelectedPatients([]);
        setSelectedCareProvider(null);
      } else {
        setError(
          `Assigned ${successCount} patient(s), but ${failCount} failed`,
        );
      }
    } catch (err: any) {
      setError("Bulk assignment failed");
    } finally {
      setBulkAssigning(false);
    }
  };

  const getTabLabel = (count: number, label: string) => {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <span>{label}</span>
        <Chip label={count} size='small' />
      </Box>
    );
  };

  const filterPatientsByPrograms = (patients: ReconciliationMatch[]) => {
    return patients.filter((patient) => {
      // If no programs are selected, show all
      if (
        !programFilters.diabetes &&
        !programFilters.weight &&
        !programFilters.hypertension
      ) {
        return true;
      }

      // Show patient if they're enrolled in any of the selected programs
      return (
        (programFilters.diabetes && patient.diabetesProgram) ||
        (programFilters.weight && patient.weightProgram) ||
        (programFilters.hypertension && patient.hypertensionProgram)
      );
    });
  };

  const toggleProgramFilter = (
    program: "diabetes" | "weight" | "hypertension",
  ) => {
    setProgramFilters((prev) => ({
      ...prev,
      [program]: !prev[program],
    }));
    // Reset selection when filters change
    setSelectedPatients([]);
  };

  return (
    <Layout>
      <Box>
        <Typography variant='h4' gutterBottom>
          Patient Reconciliation
        </Typography>
        <Typography variant='body2' color='text.secondary' paragraph>
          Reconcile patients between TNS and CCM systems. This will match
          patients by name and date of birth, and automatically store CCM
          patient IDs for matched records.
        </Typography>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {/* Step 1: Entity Selection */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant='h6' gutterBottom>
              1. Select Entity
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              Choose which CCM-enabled entity you want to reconcile.
            </Typography>
            <Select
              value={selectedEntity}
              onChange={(e) => {
                setSelectedEntity(Number(e.target.value));
                // Reset results when entity changes
                setResult(null);
                setTabValue(0);
              }}
              fullWidth
              displayEmpty
            >
              <MenuItem value={0}>Select an entity...</MenuItem>
              {clients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.companyName} ({client.id})
                </MenuItem>
              ))}
            </Select>
          </CardContent>
        </Card>

        {/* Step 2: Choose mode */}
        {selectedEntity > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                2. Choose Action
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                Select what you would like to do for this entity.
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 2,
                }}
              >
                {/* Option A: Reconcile Patients */}
                <Card
                  variant='outlined'
                  onClick={() => {
                    setStep2Mode("reconcile");
                    setCompareResult(null);
                  }}
                  sx={{
                    cursor: "pointer",
                    border: 2,
                    borderColor:
                      step2Mode === "reconcile" ? "primary.main" : "divider",
                    bgcolor:
                      step2Mode === "reconcile"
                        ? "primary.50"
                        : "background.paper",
                    transition: "all 0.15s",
                    "&:hover": { borderColor: "primary.main" },
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <SyncIcon
                        color={step2Mode === "reconcile" ? "primary" : "action"}
                      />
                      <Typography variant='subtitle1' fontWeight={600}>
                        Reconcile Patients
                      </Typography>
                    </Box>
                    <Typography variant='body2' color='text.secondary'>
                      Compare TNS and CCM patient records by name and DOB.
                      Automatically stores CCM patient IDs for matched records.
                    </Typography>
                    {step2Mode === "reconcile" && (
                      <Box sx={{ mt: 2 }}>
                        <Button
                          variant='contained'
                          startIcon={
                            loading ? (
                              <CircularProgress size={20} />
                            ) : (
                              <SyncIcon />
                            )
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReconcile();
                          }}
                          disabled={loading}
                        >
                          {loading ? "Reconciling..." : "Reconcile Patients"}
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>

                {/* Option B: Compare Enrollment Files */}
                <Card
                  variant='outlined'
                  onClick={() => {
                    setStep2Mode("compare");
                    setResult(null);
                  }}
                  sx={{
                    cursor: "pointer",
                    border: 2,
                    borderColor:
                      step2Mode === "compare" ? "secondary.main" : "divider",
                    bgcolor:
                      step2Mode === "compare"
                        ? "secondary.50"
                        : "background.paper",
                    transition: "all 0.15s",
                    "&:hover": { borderColor: "secondary.main" },
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <CompareArrowsIcon
                        color={step2Mode === "compare" ? "secondary" : "action"}
                      />
                      <Typography variant='subtitle1' fontWeight={600}>
                        Compare Enrollment Files
                      </Typography>
                    </Box>
                    <Typography variant='body2' color='text.secondary'>
                      Upload two CSV files and find patients in one that are
                      missing from the other. Matches on Member ID (column 1).
                    </Typography>
                    {step2Mode === "compare" && (
                      <Box sx={{ mt: 2 }} onClick={(e) => e.stopPropagation()}>
                        {compareError && (
                          <Alert
                            severity='error'
                            sx={{ mb: 2 }}
                            onClose={() => setCompareError("")}
                          >
                            {compareError}
                          </Alert>
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          <Box>
                            <Typography
                              variant='caption'
                              color='text.secondary'
                              display='block'
                              gutterBottom
                            >
                              File A
                            </Typography>
                            <Button
                              variant='outlined'
                              component='label'
                              startIcon={<UploadFileIcon />}
                              size='small'
                              fullWidth
                            >
                              {fileA ? fileA.name : "Choose File A…"}
                              <input
                                type='file'
                                accept='.csv'
                                hidden
                                onChange={(e) => {
                                  setFileA(e.target.files?.[0] || null);
                                  setCompareResult(null);
                                }}
                              />
                            </Button>
                          </Box>
                          <Box>
                            <Typography
                              variant='caption'
                              color='text.secondary'
                              display='block'
                              gutterBottom
                            >
                              File B
                            </Typography>
                            <Button
                              variant='outlined'
                              component='label'
                              startIcon={<UploadFileIcon />}
                              size='small'
                              fullWidth
                            >
                              {fileB ? fileB.name : "Choose File B…"}
                              <input
                                type='file'
                                accept='.csv'
                                hidden
                                onChange={(e) => {
                                  setFileB(e.target.files?.[0] || null);
                                  setCompareResult(null);
                                }}
                              />
                            </Button>
                          </Box>
                          <Button
                            variant='contained'
                            color='secondary'
                            startIcon={<CompareArrowsIcon />}
                            onClick={handleCompare}
                            disabled={!fileA || !fileB}
                          >
                            Compare Files
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Box>
            </CardContent>
          </Card>
        )}

        {result && (
          <Paper sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant='h6' gutterBottom>
                Reconciliation Results: {result.entityName}
              </Typography>

              {result.errors.length > 0 && (
                <Alert severity='warning' sx={{ mb: 2 }}>
                  <Typography variant='subtitle2' gutterBottom>
                    Errors occurred during reconciliation:
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {result.errors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </Alert>
              )}

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                <Chip
                  icon={<CheckCircleIcon />}
                  label={`Matched: ${result.matched.length}`}
                  color='success'
                  variant='outlined'
                />
                <Chip
                  icon={<WarningIcon />}
                  label={`TNS Only: ${result.tnsOnly.length}`}
                  color='warning'
                  variant='outlined'
                />
                <Chip
                  icon={<ErrorIcon />}
                  label={`CCM Only: ${result.ccmOnly.length}`}
                  color='error'
                  variant='outlined'
                />
                {result.storedCount > 0 && (
                  <Chip
                    icon={<SyncIcon />}
                    label={`CCM IDs Stored: ${result.storedCount}`}
                    color='primary'
                  />
                )}
              </Box>
            </Box>

            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
              <Tab label={getTabLabel(result.matched.length, "Matched")} />
              <Tab label={getTabLabel(result.tnsOnly.length, "TNS Only")} />
              <Tab label={getTabLabel(result.ccmOnly.length, "CCM Only")} />
            </Tabs>

            {/* Matched Tab */}
            {tabValue === 0 && (
              <Box>
                {/* Program Filters */}
                {result.matched.length > 0 && (
                  <Box sx={{ mt: 2, mb: 1, px: 2 }}>
                    <Typography variant='subtitle2' gutterBottom>
                      Filter by Program:
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Chip
                        label='Diabetes'
                        color={programFilters.diabetes ? "primary" : "default"}
                        onClick={() => toggleProgramFilter("diabetes")}
                        variant={
                          programFilters.diabetes ? "filled" : "outlined"
                        }
                      />
                      <Chip
                        label='Weight Management'
                        color={programFilters.weight ? "primary" : "default"}
                        onClick={() => toggleProgramFilter("weight")}
                        variant={programFilters.weight ? "filled" : "outlined"}
                      />
                      <Chip
                        label='Hypertension'
                        color={
                          programFilters.hypertension ? "primary" : "default"
                        }
                        onClick={() => toggleProgramFilter("hypertension")}
                        variant={
                          programFilters.hypertension ? "filled" : "outlined"
                        }
                      />
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ alignSelf: "center", ml: 1 }}
                      >
                        Showing{" "}
                        {filterPatientsByPrograms(result.matched).length} of{" "}
                        {result.matched.length} patients
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Bulk Operations Toolbar */}
                {result.matched.length > 0 && (
                  <Toolbar
                    sx={{
                      mt: 2,
                      pl: { sm: 2 },
                      pr: { xs: 1, sm: 1 },
                      bgcolor:
                        selectedPatients.length > 0
                          ? "action.selected"
                          : "transparent",
                    }}
                  >
                    {selectedPatients.length > 0 ? (
                      <>
                        <Typography
                          sx={{ flex: "1 1 100%" }}
                          color='inherit'
                          variant='subtitle1'
                          component='div'
                        >
                          {selectedPatients.length} patient(s) selected
                        </Typography>
                        <Autocomplete
                          options={careProviders}
                          getOptionLabel={(option) =>
                            `${option.fname} ${option.lname}`
                          }
                          value={selectedCareProvider}
                          onChange={(event, newValue) =>
                            setSelectedCareProvider(newValue)
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label='Care Provider'
                              size='small'
                            />
                          )}
                          sx={{ minWidth: 250, mr: 2 }}
                          disabled={bulkAssigning}
                        />
                        <Button
                          variant='contained'
                          startIcon={
                            bulkAssigning ? (
                              <CircularProgress size={20} />
                            ) : (
                              <GroupAddIcon />
                            )
                          }
                          onClick={handleBulkAssign}
                          disabled={bulkAssigning || !selectedCareProvider}
                        >
                          {bulkAssigning
                            ? "Assigning..."
                            : "Assign to Provider"}
                        </Button>
                      </>
                    ) : (
                      <Typography
                        sx={{ flex: "1 1 100%" }}
                        variant='subtitle2'
                        component='div'
                        color='text.secondary'
                      >
                        Select patients to perform bulk operations
                      </Typography>
                    )}
                  </Toolbar>
                )}

                {/* Success/Error Messages */}
                {bulkSuccess && (
                  <Alert
                    severity='success'
                    sx={{ mx: 2, mt: 2 }}
                    onClose={() => setBulkSuccess("")}
                  >
                    {bulkSuccess}
                  </Alert>
                )}

                <TableContainer sx={{ mt: 2 }}>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell padding='checkbox'>
                          <Checkbox
                            indeterminate={
                              selectedPatients.length > 0 &&
                              selectedPatients.length <
                                filterPatientsByPrograms(result.matched).length
                            }
                            checked={
                              filterPatientsByPrograms(result.matched).length >
                                0 &&
                              selectedPatients.length ===
                                filterPatientsByPrograms(result.matched).length
                            }
                            onChange={handleSelectAll}
                          />
                        </TableCell>
                        <TableCell>TNS ID</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>DOB</TableCell>
                        <TableCell>External ID</TableCell>
                        <TableCell>CCM Patient ID</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Programs</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filterPatientsByPrograms(result.matched).map(
                        (patient) => (
                          <TableRow
                            key={patient.patientID}
                            hover
                            onClick={() =>
                              handleTogglePatient(patient.patientID)
                            }
                            role='checkbox'
                            selected={selectedPatients.includes(
                              patient.patientID,
                            )}
                            sx={{ cursor: "pointer" }}
                          >
                            <TableCell padding='checkbox'>
                              <Checkbox
                                checked={selectedPatients.includes(
                                  patient.patientID,
                                )}
                              />
                            </TableCell>
                            <TableCell>{patient.patientID}</TableCell>
                            <TableCell>
                              {patient.lastname}, {patient.firstname}
                            </TableCell>
                            <TableCell>{patient.dob}</TableCell>
                            <TableCell>
                              {patient.externalLookup || "-"}
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant='caption'
                                sx={{ fontFamily: "monospace" }}
                              >
                                {patient.ccmPatientId}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {patient.tnsHasExternalSystemId ? (
                                <Chip
                                  label='Already Stored'
                                  size='small'
                                  color='default'
                                />
                              ) : (
                                <Chip
                                  label='Stored Now'
                                  size='small'
                                  color='success'
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 0.5,
                                  flexWrap: "wrap",
                                }}
                              >
                                {patient.diabetesProgram && (
                                  <Chip
                                    label='DM'
                                    size='small'
                                    color='primary'
                                    variant='outlined'
                                  />
                                )}
                                {patient.weightProgram && (
                                  <Chip
                                    label='WM'
                                    size='small'
                                    color='secondary'
                                    variant='outlined'
                                  />
                                )}
                                {patient.hypertensionProgram && (
                                  <Chip
                                    label='HTN'
                                    size='small'
                                    color='error'
                                    variant='outlined'
                                  />
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ),
                      )}
                      {filterPatientsByPrograms(result.matched).length ===
                        0 && (
                        <TableRow>
                          <TableCell colSpan={8} align='center'>
                            <Typography variant='body2' color='text.secondary'>
                              No patients match the selected program filters
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* TNS Only Tab */}
            {tabValue === 1 && (
              <Box sx={{ mt: 2 }}>
                <Alert severity='warning' sx={{ mb: 2 }}>
                  These patients exist in TNS but were not found in CCM. They
                  may need to be imported into CCM.
                </Alert>
                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>TNS ID</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>DOB</TableCell>
                        <TableCell>External ID</TableCell>
                        <TableCell>Programs</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.tnsOnly.map((patient) => (
                        <TableRow key={patient.patientID}>
                          <TableCell>{patient.patientID}</TableCell>
                          <TableCell>
                            {patient.lastname}, {patient.firstname}
                          </TableCell>
                          <TableCell>{patient.dob}</TableCell>
                          <TableCell>{patient.externalLookup || "-"}</TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.5,
                                flexWrap: "wrap",
                              }}
                            >
                              {patient.diabetesProgram && (
                                <Chip
                                  label='DM'
                                  size='small'
                                  color='primary'
                                  variant='outlined'
                                />
                              )}
                              {patient.weightProgram && (
                                <Chip
                                  label='WM'
                                  size='small'
                                  color='secondary'
                                  variant='outlined'
                                />
                              )}
                              {patient.hypertensionProgram && (
                                <Chip
                                  label='HTN'
                                  size='small'
                                  color='error'
                                  variant='outlined'
                                />
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                      {result.tnsOnly.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align='center'>
                            <Typography variant='body2' color='text.secondary'>
                              No TNS-only patients
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* CCM Only Tab */}
            {tabValue === 2 && (
              <Box sx={{ mt: 2 }}>
                <Alert severity='info' sx={{ mb: 2 }}>
                  These patients exist in CCM but were not found in TNS. They
                  may have been created directly in CCM or have mismatched
                  information.
                </Alert>
                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>CCM Patient ID</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>DOB</TableCell>
                        <TableCell>Email</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.ccmOnly.map((patient) => (
                        <TableRow key={patient.ccmPatientId}>
                          <TableCell>
                            <Typography
                              variant='caption'
                              sx={{ fontFamily: "monospace" }}
                            >
                              {patient.ccmPatientId}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {patient.lastname}, {patient.firstname}
                          </TableCell>
                          <TableCell>{patient.dob}</TableCell>
                          <TableCell>{patient.email || "-"}</TableCell>
                        </TableRow>
                      ))}
                      {result.ccmOnly.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align='center'>
                            <Typography variant='body2' color='text.secondary'>
                              No CCM-only patients
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Paper>
        )}

        {/* Compare Enrollment Files Results */}
        {compareResult && (
          <Paper sx={{ p: 3 }}>
            <Typography variant='h6' gutterBottom>
              Comparison Results
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              <strong>File A:</strong> {compareResult.fileAName} &nbsp;|&nbsp;
              <strong>File B:</strong> {compareResult.fileBName}
            </Typography>

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
              <Chip
                icon={<WarningIcon />}
                label={`In A only: ${compareResult.aOnly.length}`}
                color='warning'
                variant='outlined'
              />
              <Chip
                icon={<ErrorIcon />}
                label={`In B only: ${compareResult.bOnly.length}`}
                color='error'
                variant='outlined'
              />
              <Chip
                icon={<CheckCircleIcon />}
                label={`In both: ${compareResult.inBoth.length}`}
                color='success'
                variant='outlined'
              />
            </Box>

            <Tabs
              value={compareTabValue}
              onChange={(_, v) => setCompareTabValue(v)}
              sx={{ mb: 1 }}
            >
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <span>In A Only</span>
                    <Chip
                      label={compareResult.aOnly.length}
                      size='small'
                      color='warning'
                    />
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <span>In B Only</span>
                    <Chip
                      label={compareResult.bOnly.length}
                      size='small'
                      color='error'
                    />
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <span>In Both</span>
                    <Chip
                      label={compareResult.inBoth.length}
                      size='small'
                      color='success'
                    />
                  </Box>
                }
              />
            </Tabs>

            {[
              {
                rows: compareResult.aOnly,
                emptyMsg: "No patients found only in File A",
              },
              {
                rows: compareResult.bOnly,
                emptyMsg: "No patients found only in File B",
              },
              {
                rows: compareResult.inBoth,
                emptyMsg: "No patients found in both files",
              },
            ].map(({ rows, emptyMsg }, tabIdx) =>
              compareTabValue === tabIdx ? (
                <TableContainer key={tabIdx}>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Member ID</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Last Name</strong>
                        </TableCell>
                        <TableCell>
                          <strong>First Name</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} align='center'>
                            <Typography variant='body2' color='text.secondary'>
                              {emptyMsg}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        rows.map((row, i) => (
                          <TableRow key={i} hover>
                            <TableCell
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.85rem",
                              }}
                            >
                              {row.memberId.toUpperCase()}
                            </TableCell>
                            <TableCell sx={{ textTransform: "capitalize" }}>
                              {row.lastname}
                            </TableCell>
                            <TableCell sx={{ textTransform: "capitalize" }}>
                              {row.firstname}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : null,
            )}
          </Paper>
        )}
      </Box>
    </Layout>
  );
};

export default PatientReconciliation;
